import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import dayjs from "dayjs";
import { db, eq, ilike } from "../lib/api";
import { invalidate, useDebounced, useQuery } from "../lib/hooks";
import { AREA_LABEL, LEAD_AREAS, useRefData, type Area } from "../lib/refdata";
import { useSession } from "../lib/session";
import { navigate } from "../lib/router";
import { ErrorBox, Field, Select, Sheet, useToast } from "./ui";
import { openPerson } from "./PersonDrawer";
import { APP_STATUSES, CoverageFields, PremiumFields, isInsuranceType } from "./Coverage";
import { ConflictNote } from "./Appointments";
import { zoneLocal, zoneToUtc, type Zone } from "../lib/zones";
import { MeetingWhen, meetingIso } from "./MeetingWhen";
import { RemindMeButton } from "./Bell";

export type OppRow = { id: string; person_id: string; full_name: string; source_area: Area; board: string; stage: string; next_action: string | null; next_action_on: string | null; est_value: number | null; outcome: string; notes: string | null; [k: string]: any };

/** "Add to pipeline" from anywhere: call useAddToPipeline()({ person_id, full_name, source_area }). */
const Ctx = createContext<(p: OppPreset) => void>(() => undefined);
export const useAddToPipeline = () => useContext(Ctx);
export function PipelineProvider({ children }: { children: ReactNode }) {
  const [preset, setPreset] = useState<OppPreset | null>(null);
  const { can } = useSession();
  const open = useCallback((p: OppPreset) => { if (can("pipelines", "edit")) setPreset(p); }, [can]);
  return (
    <Ctx.Provider value={open}>
      {children}
      {preset && <OppSheet opp={null} preset={preset} onClose={() => setPreset(null)} />}
    </Ctx.Provider>
  );
}

export type OppPreset = { person_id: string; full_name: string; source_area?: Area; board?: string };

export function OppSheet({ opp, board, preset, onClose }: { opp: OppRow | null; board?: string; preset?: OppPreset; onClose: () => void }) {
  const ref = useRefData();
  const { can } = useSession();
  const toast = useToast();
  const startBoard = preset?.board ?? board ?? ref.data?.boards[0] ?? "Solution";
  const [f, setF] = useState<any>(opp ?? { board: startBoard, stage: ref.data?.stagesFor(startBoard)[0], source_area: preset?.source_area ?? "warm", outcome: "Open" });
  const [person, setPerson] = useState<{ id: string; name: string } | null>(
    opp ? { id: opp.person_id, name: opp.full_name } : preset ? { id: preset.person_id, name: preset.full_name } : null);
  const [q, setQ] = useState("");
  const dq = useDebounced(q);
  const found = useQuery(dq.length > 1 ? `opp-find:${dq}` : null, () => db.select<{ id: string; full_name: string }>("people", { select: "id,full_name", full_name: ilike(dq), limit: 8 }));
  const [error, setError] = useState<Error | null>(null);
  const set = (k: string) => (v: string) => setF((x: any) => ({ ...x, [k]: v || null }));
  // "Booked" means scheduled: entering a booked stage asks for the meeting time and puts it in Meetings.
  const isBooked = /booked/i.test(f.stage ?? "") && f.outcome === "Open";
  const needsMeeting = isBooked && (!opp || (opp._from ?? opp.stage) !== f.stage);
  const [meetAt, setMeetAt] = useState("");
  const [meetZone, setMeetZone] = useState<Zone>("ON");
  const { profile } = useSession();
  const dealAdmin = profile?.role_key === "owner" || can("people_admin", "edit");
  const wonDeal = !!opp && (opp.outcome === "Won" || !!ref.data?.isWon(opp.board, opp.stage));
  const [confirmDel, setConfirmDel] = useState(false);
  const [moving, setMoving] = useState(false);

  async function remove() {
    if (!opp) return;
    setError(null);
    try {
      const r = await db.rpc<{ ok: boolean; reason?: string }>("deal_delete", { p_id: opp.id });
      if (!r.ok) throw new Error(r.reason ?? "Couldn't delete this deal");
      invalidate("pipeline"); invalidate("today"); invalidate(`person:${opp.person_id}`); invalidate("dash:"); invalidate("cc"); invalidate("clients"); invalidate("removed");
      toast("Deal deleted · it's in Removed for 30 days"); onClose();
    } catch (e) { setError(e as Error); setConfirmDel(false); }
  }
  async function moveTo(p: { id: string; name: string }) {
    if (!opp) return;
    setError(null);
    try {
      const r = await db.rpc<{ ok: boolean; reason?: string }>("deal_move", { p_id: opp.id, p_person: p.id });
      if (!r.ok) throw new Error(r.reason ?? "Couldn't move this deal");
      invalidate("pipeline"); invalidate("today"); invalidate(`person:${opp.person_id}`); invalidate(`person:${p.id}`); invalidate("cc"); invalidate("clients");
      toast(`Deal moved to ${p.name}`); onClose();
    } catch (e) { setError(e as Error); }
  }

  async function save() {
    setError(null);
    try {
      if (!person) throw new Error("Choose who this opportunity is with.");
      if (needsMeeting && !meetAt) throw new Error("When is the appointment? Booked means it has a date and time.");
      if (needsMeeting && !f.next_action) f.next_action = `Hold the ${String(f.stage).replace(/ booked/i, "").toLowerCase()}`;
      if (needsMeeting && !f.next_action_on) f.next_action_on = dayjs(meetAt).format("YYYY-MM-DD");
      if (f.outcome === "Open" && (!f.next_action || !f.next_action_on))
        throw new Error("Every open card needs a next action and a date. If there's nothing to do, set the outcome to Not now.");
      const body = {
        person_id: person.id, board: f.board, stage: f.stage, source_area: f.source_area, next_action: f.next_action,
        next_action_on: f.next_action_on, est_value: f.est_value ? Number(f.est_value) : null, outcome: f.outcome,
        closed_on: f.outcome !== "Open" ? f.closed_on || dayjs().format("YYYY-MM-DD") : null,
        life_premium: f.life_premium ? Number(f.life_premium) : null, points: f.points ? Number(f.points) : null,
        lost_reason: f.outcome === "Lost" ? f.lost_reason : null, notes: f.notes,
        solutions: f.board === "Solution" ? (f.solutions ?? []).filter((x: Sol) => x.solution_type) : [],
      };
      if (opp) await db.update("opportunities", { id: eq(opp.id) }, body);
      else await db.insert("opportunities", body);
      if (needsMeeting) {
        await db.insert("appointments", { person_id: person.id, title: f.stage, starts_at: zoneToUtc(meetAt, meetZone),
          ends_at: dayjs(zoneToUtc(meetAt, meetZone)).add(1, "hour").toISOString(), source: "crm", remind: true, zone_code: meetZone });
        invalidate("appts");
        db.rpc("google_sync_now").catch(() => undefined);
        db.rpc("meeting_emails_now").catch(() => undefined);
      }
      invalidate("pipeline"); invalidate("today"); invalidate(`person:${person.id}`); invalidate("people"); invalidate("dash:"); invalidate("cc");
      const won = f.board === "Solution" && (f.outcome === "Won" || ref.data?.isWon(f.board, f.stage));
      if (won) { invalidate("clients"); invalidate(`client`); }
      toast(won ? `${person.name} is now a client${(body.solutions ?? []).length ? " · solutions added to their profile" : ""}` : opp ? "Opportunity saved" : "Opportunity added");
      onClose();
    } catch (e) { setError(e as Error); }
  }

  return (
    <Sheet title={opp ? opp.full_name : preset ? `Add ${preset.full_name} to a pipeline` : "Add opportunity"} onClose={onClose} footer={<>
      {opp && <button className="btn ghost" onClick={() => openPerson(opp.person_id)}>Open person</button>}
      {opp && f.outcome === "Won" && can("sensitive") && <button className="btn ghost" title="Reason Why Letter, Coverage Summary and Meeting Notes" onClick={() => { onClose(); navigate(`/clients/${opp.person_id}/file?opp=${opp.id}`); }}>Client file</button>}
      {opp && <RemindMeButton opportunityId={opp.id} person={{ id: opp.person_id, full_name: opp.full_name }} />}
      {opp && dealAdmin && !wonDeal && (confirmDel
        ? <span className="del-confirm">Delete this deal? <button className="btn small danger" onClick={remove}>Yes, delete</button> <button className="btn small ghost" onClick={() => setConfirmDel(false)}>Keep</button></span>
        : <button className="btn ghost" style={{ color: "var(--alert)" }} title="For a mistake or duplicate. For a real result use Lost or Not now." onClick={() => setConfirmDel(true)}>Delete deal</button>)}
      <button className="btn primary" onClick={save}>{opp ? "Save" : "Add opportunity"}</button>
    </>}>
      <ErrorBox error={error} />
      {opp && !wonDeal && can("pipelines", "edit") && (moving
        ? <Field label="Move this deal to"><PersonFind exclude={opp.person_id} onPick={moveTo} />
            <span className="small muted">Its applications and linked tasks move too. <button className="btn small ghost" onClick={() => setMoving(false)}>Cancel</button></span></Field>
        : <div className="small muted" style={{ marginBottom: 8 }}>Deal with <b>{opp.full_name}</b> · <button className="btn small ghost" onClick={() => setMoving(true)}>Move to another person</button></div>)}
      {!opp && (person ? <Field label="Person"><div><strong>{person.name}</strong> <button className="btn small ghost" onClick={() => setPerson(null)}>Change</button></div></Field> : (
        <Field label="Person">
          <input className="input" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a name" />
          {(found.data ?? []).map((p) => <button key={p.id} className="btn ghost" style={{ justifyContent: "flex-start" }} onClick={() => setPerson({ id: p.id, name: p.full_name })}>{p.full_name}</button>)}
        </Field>
      ))}
      <div className="form-grid">
        <Field label="Board"><Select value={f.board} onChange={(v) => setF((x: any) => ({ ...x, board: v, stage: ref.data?.stagesFor(v)[0] }))}
          options={(ref.data?.boards ?? []).map((b) => ({ value: b, label: b === "Solution" ? "Solution · a sale" : b === "Business" ? "Business · joining the team" : b }))} /></Field>
        <Field label="Stage"><Select value={f.stage} onChange={set("stage")} options={ref.data?.stagesFor(f.board) ?? []} /></Field>
        <Field label="Came from"><Select value={f.source_area} onChange={set("source_area")} options={LEAD_AREAS.map((a) => ({ value: a, label: AREA_LABEL[a] }))} /></Field>
        {needsMeeting && <Field label="Appointment date, time and time zone" wide><MeetingWhen value={meetAt} zone={meetZone} onChange={(v, z) => { setMeetAt(v); setMeetZone(z); }} />
          <span className="small muted">It goes into Meetings and Google Calendar, and the client gets the reminder emails.</span>
          <ConflictNote start={meetAt ? zoneLocal(meetingIso(meetAt, meetZone), "ON") : meetAt} /></Field>}
        <Field label={f.outcome === "Open" ? "Next action (required)" : "Next action"} wide><input className="input" value={f.next_action ?? ""} onChange={(e) => set("next_action")(e.target.value)} /></Field>
        <Field label={f.outcome === "Open" ? "Next action date (required)" : "Next action date"}><input type="date" className="input" value={f.next_action_on ?? ""} onChange={(e) => set("next_action_on")(e.target.value)} /></Field>
        <Field label="Est. value ($)"><input type="number" className="input" value={f.est_value ?? ""} onChange={(e) => set("est_value")(e.target.value)} /></Field>
        <Field label="Outcome"><Select value={f.outcome} onChange={(v) => setF((x: any) => { const won = (ref.data?.stagesFor(x.board) ?? []).find((s) => ref.data?.isWon(x.board, s));
            return { ...x, outcome: v || null, ...(v === "Won" && won && !ref.data?.isWon(x.board, x.stage) ? { stage: won } : {}) }; })} options={ref.data?.list("Outcomes") ?? ["Open", "Won", "Lost", "Not now"]} /></Field>
        {f.outcome === "Won" && <>
          <Field label="Won date"><input type="date" className="input" value={f.closed_on ?? ""} onChange={(e) => set("closed_on")(e.target.value)} /></Field>
          <Field label="Life premium ($)"><input type="number" className="input" value={f.life_premium ?? ""} onChange={(e) => set("life_premium")(e.target.value)} /></Field>
          <Field label="Points"><input type="number" className="input" value={f.points ?? ""} onChange={(e) => set("points")(e.target.value)} /></Field>
        </>}
        {f.outcome === "Lost" && <Field label="Lost reason"><Select value={f.lost_reason} onChange={set("lost_reason")} options={ref.data?.list("LostReasons") ?? []} /></Field>}
        <Field label="Notes" wide><textarea className="textarea" value={f.notes ?? ""} onChange={(e) => set("notes")(e.target.value)} /></Field>
      </div>
      {f.board === "Solution" && <SolutionsEditor personId={person?.id} value={f.solutions ?? []} onChange={(v) => setF((x: any) => ({ ...x, solutions: v }))} defaultStatus={/submitted/i.test(f.stage ?? "") ? "Submitted" : undefined} />}
    </Sheet>
  );
}

export type Sol = { key: string; solution_type: string | null; provider: string | null; status?: string; premium?: string; premium_frequency?: string;
  premium_pay_period?: string; permanent_type?: string; permanent_amount?: string; term_type?: string; term_amount?: string; ci_type?: string; ci_amount?: string;
  other_riders?: string; riders?: any[]; contract_no?: string; coverage?: string; term?: string; deposit?: string; pac_amount?: string; pac_frequency?: string; product?: string; insured_person_id?: string };
const newKey = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));
export const SOL_STATUSES = [{ value: "Proposed", label: "Proposed (not applied for yet)" }, ...APP_STATUSES.map((v) => ({ value: v, label: v }))];

/** Older deals kept one coverage amount and a term in years: show them in the new fields. */
function upgrade(s: Sol): Sol {
  if (s.permanent_type || s.term_type || s.ci_type || (!s.coverage && !s.term)) return s;
  const t = s.solution_type ?? "", n = s.term?.match(/\d+/)?.[0];
  const out: Sol = { ...s, coverage: undefined, term: undefined };
  if (/Permanent|Whole/.test(t)) Object.assign(out, { permanent_type: /Whole/.test(t) ? "Whole Life" : "UL", permanent_amount: s.coverage });
  else if (/Critical/.test(t)) Object.assign(out, { ci_type: n ? `CI${n}` : undefined, ci_amount: s.coverage });
  else if (/Term|Mortgage/.test(t)) Object.assign(out, { term_type: n ? `T${n}` : undefined, term_amount: s.coverage });
  return out;
}

/** The products in a Solution deal. Applied-for ones go on the person's file straight away
 *  (not counted until Active); when the deal is Won (or reaches Placed) they all become Active. */
export function SolutionsEditor({ value, onChange, defaultStatus, personId }: { value: Sol[]; onChange: (v: Sol[]) => void; defaultStatus?: string; personId?: string }) {
  const ref = useRefData();
  const lives = useLives(personId);
  const [addingFor, setAddingFor] = useState<number | null>(null);
  const types = ref.data?.list("SolutionTypes") ?? [];
  const providers = ref.data?.list("Providers") ?? [];
  const freq = ref.data?.list("PayFrequency") ?? ["Monthly", "Annual"];
  const rows = value.map(upgrade);
  const upd = (i: number, k: keyof Sol, v: any) => onChange(rows.map((x, j) => (j === i ? { ...x, [k]: v === "" || v == null ? undefined : v } : x)));
  return (
    <div className="sol-editor">
      <div className="sol-head">
        <div><strong>Solutions in this deal</strong>
          <div className="small muted">Once applied for, they show on the person's file as an application (not counted). When the deal is Won or Placed they become Active.</div></div>
        <button className="btn small" onClick={() => onChange([...rows, { key: newKey(), solution_type: null, provider: null, status: defaultStatus ?? "Proposed" }])}>+ Add solution</button>
      </div>
      {rows.length === 0 && <div className="small muted" style={{ padding: "6px 0" }}>None yet. Add each product: e.g. UL with Equitable plus a T20 rider, and a TFSA.</div>}
      {rows.map((s, i) => {
        const ins = isInsuranceType(s.solution_type);
        const set = (k: string) => (v: any) => upd(i, k as keyof Sol, v);
        return (
          <div key={s.key} className="sol-row">
            <div className="form-grid">
              <Field label="Solution"><Select value={s.solution_type ?? ""} onChange={(v) => upd(i, "solution_type", v)} options={types} /></Field>
              <Field label="Provider"><Select value={s.provider ?? ""} onChange={(v) => upd(i, "provider", v)} options={providers} /></Field>
              <Field label="Application"><Select value={s.status ?? "Proposed"} onChange={(v) => upd(i, "status", v || "Proposed")} options={SOL_STATUSES} /></Field>
              <Field label="Product / plan name"><input className="input" value={s.product ?? ""} onChange={(e) => upd(i, "product", e.target.value)} placeholder="e.g. EquiLiving, Transace" /></Field>
              {personId && <Field label={s.solution_type && !ins ? "For (e.g. RESP child)" : "Life insured"}>
                <Select value={s.insured_person_id ?? ""} placeholder={`Same as owner${lives.owner ? ` (${lives.owner})` : ""}`}
                  onChange={(v) => { if (v === "__new") setAddingFor(i); else { setAddingFor(null); upd(i, "insured_person_id", v); } }}
                  options={[...lives.others, { value: "__new", label: "+ Add a child…" }]} />
                {s.insured_person_id && <span className="small muted">{lives.owner ?? "The owner"} owns and pays; this person is covered.</span>}
              </Field>}
              {personId && addingFor === i && <AddChild parentId={personId} onDone={(id) => { setAddingFor(null); if (id) upd(i, "insured_person_id", id); }} />}
              {s.status && s.status !== "Proposed" && <Field label="Application / policy number"><input className="input mono" value={s.contract_no ?? ""} onChange={(e) => upd(i, "contract_no", e.target.value)} /></Field>}
              {s.solution_type && (ins ? <>
                <PremiumFields f={s} set={set} />
                {!/health|travel|group|dental/i.test(s.solution_type!) && <CoverageFields f={s} set={set} />}
              </> : <>
                <Field label="Opening deposit ($)"><input type="number" className="input" value={s.deposit ?? ""} onChange={(e) => upd(i, "deposit", e.target.value)} /></Field>
                <Field label="Regular contribution ($)"><input type="number" className="input" value={s.pac_amount ?? ""} onChange={(e) => upd(i, "pac_amount", e.target.value)} /></Field>
                <Field label="Every"><Select value={s.pac_frequency ?? "Monthly"} onChange={(v) => upd(i, "pac_frequency", v)} options={freq} /></Field>
              </>)}
            </div>
            <button className="btn small ghost" aria-label="Remove solution" onClick={() => onChange(rows.filter((_, j) => j !== i))}>Remove</button>
          </div>
        );
      })}
    </div>
  );
}

/** The deal owner's family (from Family & links, incl. a spouse's children): who a solution can cover. */
function useLives(personId?: string) {
  const ref = useRefData();
  // the link is stored from the owner's side ("Parent of"); show the other person's side ("child")
  const theirSide = (key: string, label: string) => {
    const t = ref.data?.relTypes.find((r) => r.key === key);
    const inv = t?.inverse_key ? ref.data?.relTypes.find((r) => r.key === t.inverse_key) : t;
    return (inv?.label ?? label).replace(/\s+(of|to|for|by)$/i, "").toLowerCase();
  };
  const q = useQuery(personId ? `person:${personId}:lives` : null, async () => {
    const [me] = await db.select<{ id: string; full_name: string; last_name: string | null }>("people", { id: eq(personId!), select: "id,full_name,last_name" });
    const links = await db.select<{ other_person_id: string; type_key: string; label: string }>("person_links", { person_id: eq(personId!), select: "other_person_id,type_key,label" });
    const ids = [...new Set(links.map((l) => l.other_person_id))];
    const ppl = ids.length ? await db.select<{ id: string; full_name: string }>("people", { id: `in.(${ids.join(",")})`, deleted_at: "is.null", select: "id,full_name", order: "full_name" }) : [];
    return { me, links, ppl };
  });
  const others = (q.data?.ppl ?? []).map((p) => { const l = q.data!.links.find((x) => x.other_person_id === p.id);
    return { value: p.id, label: l ? `${p.full_name} (${theirSide(l.type_key, l.label)})` : p.full_name }; });
  return { owner: q.data?.me?.full_name, lastName: q.data?.me?.last_name ?? "", others };
}

/** Add a child under the deal owner right inside the deal: a new person linked as their child. */
function AddChild({ parentId, onDone }: { parentId: string; onDone: (id: string | null) => void }) {
  const { can } = useSession();
  const ref = useRefData();
  const toast = useToast();
  const lives = useLives(parentId);
  const [first, setFirst] = useState("");
  const [last, setLast] = useState<string | null>(null);
  const [dob, setDob] = useState("");
  const [gender, setGender] = useState("");
  const [err, setErr] = useState<Error | null>(null);
  async function save() {
    setErr(null);
    try {
      if (!first.trim()) throw new Error("Type the child's first name");
      const d = dob ? dayjs(dob) : null;
      const [p] = await db.insert<{ id: string }>("people", { first_name: first.trim(), last_name: (last ?? lives.lastName).trim() || null,
        ...(d ? { birth_month: d.month() + 1, birth_day: d.date() } : {}), ...(gender ? { gender } : {}) });
      await db.insert("relationships", { from_person: parentId, to_person: p.id, type_key: "parent_of" });
      if (dob && can("sensitive", "edit")) await db.upsert("people_private", { person_id: p.id, date_of_birth: dob }, "person_id").catch(() => undefined);
      invalidate(`person:${parentId}`); invalidate("people");
      toast(`${first.trim()} added as their child`);
      onDone(p.id);
    } catch (e) { setErr(e as Error); }
  }
  return (
    <div className="add-child" style={{ gridColumn: "1 / -1" }}>
      <ErrorBox error={err} />
      <div className="form-grid">
        <Field label="Child's first name"><input className="input" autoFocus value={first} onChange={(e) => setFirst(e.target.value)} /></Field>
        <Field label="Last name"><input className="input" value={last ?? lives.lastName} onChange={(e) => setLast(e.target.value)} /></Field>
        <Field label="Date of birth"><input type="date" className="input" value={dob} onChange={(e) => setDob(e.target.value)} /></Field>
        <Field label="Gender"><Select value={gender} onChange={setGender} options={ref.data?.list("Gender") ?? ["Female", "Male"]} placeholder="—" /></Field>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn small primary" onClick={save}>Add child</button>
        <button className="btn small ghost" onClick={() => onDone(null)}>Cancel</button>
      </div>
      <span className="small muted">They're linked to this client as their child (Family &amp; links). The date of birth sets when the cover ends.</span>
    </div>
  );
}

/** Search a person by name. */
function PersonFind({ onPick, exclude }: { onPick: (p: { id: string; name: string }) => void; exclude?: string }) {
  const [q, setQ] = useState("");
  const dq = useDebounced(q);
  const found = useQuery(dq.length > 1 ? `opp-find:${dq}` : null, () => db.select<{ id: string; full_name: string }>("people", { select: "id,full_name", full_name: ilike(dq), limit: 8 }));
  return (<>
    <input className="input" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a name" />
    {(found.data ?? []).filter((p) => p.id !== exclude).map((p) => <button key={p.id} className="btn ghost" style={{ justifyContent: "flex-start" }} onClick={() => onPick({ id: p.id, name: p.full_name })}>{p.full_name}</button>)}
  </>);
}
